import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Link as MuiLink,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff, Google, Terminal } from '@mui/icons-material';
import { supabase } from '../config';
import { EXTERNAL_LINKS, getDeveloperPortalOrigin } from '../constants/externalLinks';
import { useAuth } from '../contexts/AuthContext';
import {
  formatWaitTime,
  getLoginGateStatus,
  getSafeRedirectPath,
  registerBlockedLogin,
  registerFailedLoginAttempt,
  registerOAuthStart,
  registerSuccessfulLogin,
} from '../services/authSecurityService';

// Styled components
const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: 20,
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.3)',
  maxWidth: 480,
  width: '100%',
  background: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
}));

const GradientButton = styled(Button)(() => ({
  background: 'linear-gradient(135deg, #ff8b42, #8f63ff)',
  color: 'white',
  padding: '14px 24px',
  borderRadius: 12,
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #ff7a2f, #7a4dff)',
    boxShadow: '0 6px 20px rgba(143, 99, 255, 0.35)',
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    background: 'linear-gradient(135deg, rgba(255, 139, 66, 0.5), rgba(143, 99, 255, 0.5))',
  },
}));

const GoogleButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  color: theme.palette.text.primary,
  border: `1px solid rgba(255, 255, 255, 0.12)`,
  padding: '14px 24px',
  borderRadius: 12,
  fontWeight: 500,
  fontSize: '1rem',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
}));

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectPath = useMemo(() => {
    const stateFrom = (location.state as any)?.from;
    const fromPath = stateFrom?.pathname
      ? `${stateFrom.pathname}${stateFrom.search || ''}${stateFrom.hash || ''}`
      : null;

    const queryNext = new URLSearchParams(location.search).get('next');
    return getSafeRedirectPath(queryNext || fromPath || '/dashboard');
  }, [location.search, location.state]);

  useEffect(() => {
    if (user) {
      navigate(redirectPath, { replace: true });
    }
  }, [user, navigate, redirectPath]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) {
      setError('Enter a valid email and password.');
      return;
    }

    setLoading(true);
    setError(null);
    setAuthNotice(null);

    const gateStatus = await getLoginGateStatus(normalizedEmail);
    if (!gateStatus.allowed) {
      const message = `Too many sign-in attempts. Try again in ${formatWaitTime(gateStatus.waitSeconds)}.`;
      setError(message);
      await registerBlockedLogin(normalizedEmail, {
        method: 'password',
        route: location.pathname,
        user_agent: navigator.userAgent,
        wait_seconds: gateStatus.waitSeconds,
      });
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
      if (error) {
        throw error;
      }

      const { data } = await supabase.auth.getUser();
      await registerSuccessfulLogin(normalizedEmail, data?.user?.id || null, {
        method: 'password',
        route: location.pathname,
        user_agent: navigator.userAgent,
      });

      navigate(redirectPath, { replace: true });
    } catch (err: any) {
      await registerFailedLoginAttempt(normalizedEmail, {
        method: 'password',
        route: location.pathname,
        user_agent: navigator.userAgent,
        reason: String(err?.message || 'unknown').slice(0, 180),
      });

      const nextGate = await getLoginGateStatus(normalizedEmail);
      if (!nextGate.allowed) {
        setError(`Too many sign-in attempts. Try again in ${formatWaitTime(nextGate.waitSeconds)}.`);
      } else {
        const remaining = nextGate.attemptsRemaining;
        const suffix = remaining <= 2 ? ` ${remaining} attempt(s) left before temporary lock.` : '';
        setError(`Invalid email or password.${suffix}`);
      }
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    setLoading(true);
    setError(null);
    setAuthNotice(null);

    try {
      if (normalizedEmail) {
        const gateStatus = await getLoginGateStatus(normalizedEmail);
        if (!gateStatus.allowed) {
          setError(`Too many sign-in attempts. Try again in ${formatWaitTime(gateStatus.waitSeconds)}.`);
          await registerBlockedLogin(normalizedEmail, {
            method: 'google_oauth',
            route: location.pathname,
            user_agent: navigator.userAgent,
            wait_seconds: gateStatus.waitSeconds,
          });
          setLoading(false);
          return;
        }
      }

      await registerOAuthStart(normalizedEmail || undefined, {
        method: 'google_oauth',
        route: location.pathname,
        user_agent: navigator.userAgent,
      });

      const origin = getDeveloperPortalOrigin();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${origin}/login?next=${encodeURIComponent(redirectPath)}`,
        }
      });
      if (error) throw error;

      setAuthNotice('Redirecting to Google secure sign-in...');
    } catch (err: any) {
      setError(err.message || 'Google login failed. Please try again.');
      console.error('Google login error:', err);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at 50% 30%, #15152a 0%, #05070a 60%)',
        padding: { xs: 2, sm: 4 },
      }}
    >
      {/* Skip to main content for accessibility */}
      <a href="#login-form" className="skip-to-content">
        Skip to login form
      </a>

      <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Logo and Header */}
        <Box mb={4} textAlign="center">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <Terminal
              sx={{
                fontSize: 40,
                color: '#ff8b42',
                filter: 'drop-shadow(0 0 8px rgba(255, 139, 66, 0.4))',
              }}
            />
            <Typography
              variant="h4"
              fontWeight="800"
              sx={{
                ml: 1.5,
                color: '#fff',
                letterSpacing: '-0.02em',
              }}
            >
              Ad<span style={{ color: '#ff8b42' }}>Mesh</span>
            </Typography>
            <Typography
              variant="caption"
              sx={{
                ml: 1.5,
                px: 1,
                py: 0.5,
                bgcolor: 'rgba(255, 139, 66, 0.15)',
                color: '#ff8b42',
                borderRadius: 1,
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Dev
            </Typography>
          </Box>
          <Typography
            variant="h5"
            fontWeight="700"
            gutterBottom
            sx={{ color: '#fff', letterSpacing: '-0.01em' }}
          >
            Developer Portal
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Sign in to access your developer dashboard
          </Typography>
        </Box>

        {/* Login Form */}
        <LoginPaper elevation={0} id="login-form">
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                '& .MuiAlert-icon': {
                  color: '#EF4444',
                },
              }}
            >
              {error}
            </Alert>
          )}
          {authNotice && (
            <Alert
              severity="info"
              sx={{
                mb: 3,
                borderRadius: 2,
                bgcolor: 'rgba(143, 99, 255, 0.12)',
                border: '1px solid rgba(143, 99, 255, 0.28)',
              }}
            >
              {authNotice}
            </Alert>
          )}

          <form onSubmit={handleLogin}>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              margin="normal"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete="email"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="current-password"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box textAlign="right" mb={3}>
              <MuiLink
                component={Link}
                to="/forgot-password"
                underline="hover"
                sx={{
                  color: '#ffae78',
                  fontSize: '0.875rem',
                  '&:hover': {
                    color: '#ff8b42',
                  },
                }}
              >
                Forgot password?
              </MuiLink>
            </Box>

            <GradientButton
              type="submit"
              fullWidth
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </GradientButton>
          </form>

          <Box my={3} display="flex" alignItems="center">
            <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.08)' }} />
            <Typography
              variant="body2"
              sx={{
                px: 2,
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '0.875rem',
              }}
            >
              OR
            </Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.08)' }} />
          </Box>

          <GoogleButton
            fullWidth
            size="large"
            startIcon={<Google />}
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            Sign in with Google
          </GoogleButton>

          <Box mt={4} textAlign="center">
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Don't have an account?{' '}
              <MuiLink
                component={Link}
                to="/signup"
                underline="hover"
                sx={{
                  color: '#ffae78',
                  fontWeight: 500,
                  '&:hover': {
                    color: '#ff8b42',
                  },
                }}
              >
                Sign up
              </MuiLink>
            </Typography>
          </Box>

          <Typography sx={{ mt: 2.5, color: 'rgba(255,255,255,0.42)', fontSize: '0.78rem', textAlign: 'center' }}>
            Protected login: repeated failures trigger temporary lockout.
          </Typography>
        </LoginPaper>

        {/* Footer Links */}
        <Box mt={4} textAlign="center">
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>
            By signing in, you agree to our{' '}
            <MuiLink href={EXTERNAL_LINKS.terms} sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Terms of Service
            </MuiLink>
            {' '}and{' '}
            <MuiLink href={EXTERNAL_LINKS.privacy} sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Privacy Policy
            </MuiLink>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
